import { useCallback, useEffect, useState } from 'react';
import { Copy, Link2, RefreshCw, Share2, X } from 'lucide-react';
import { apiMintGuestLink, apiRevokeGuestLink } from '../guestApi';
import {
  buildGuestPlayUrl,
  clearGuestToken,
  readGuestToken,
  stashGuestToken,
} from '../guestLinkStash';
import { useCurrentPlayer } from '../guestPlayer';

/**
 * The creator's half of a guest link (WS-23): shown above the board while seat 2 is still
 * empty, with the URL to send and the two things you might want to do about it.
 *
 * Tone is ambient, not demanding (`memory/no-demand-mechanics.md`): it states that the seat
 * is open and leaves it there. No countdown, no "waiting…" spinner that implies somebody is
 * late, no reminder to chase them. The board underneath stays fully visible — you can look
 * at the empty position while you wait.
 *
 * Guests never see this: they *are* the other seat.
 */
export function GuestSeatPanel({ gameId }: { gameId: string }): JSX.Element | null {
  const player = useCurrentPlayer();
  const [token, setToken] = useState<string | null>(() => readGuestToken(gameId));
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setToken(readGuestToken(gameId));
  }, [gameId]);

  const url = token ? buildGuestPlayUrl(window.location.origin, token) : null;

  const regenerate = useCallback(async () => {
    setBusy(true);
    try {
      const minted = await apiMintGuestLink(gameId);
      stashGuestToken(gameId, minted.token);
      setToken(minted.token);
    } finally {
      setBusy(false);
    }
  }, [gameId]);

  const cancel = useCallback(async () => {
    setBusy(true);
    try {
      await apiRevokeGuestLink(gameId);
      clearGuestToken(gameId);
      setToken(null);
    } finally {
      setBusy(false);
    }
  }, [gameId]);

  const copy = useCallback(async () => {
    if (!url) return;
    // Share sheet where there is one (a phone, mid-conversation), clipboard otherwise.
    const nav = navigator as Navigator & { share?: (data: { url: string }) => Promise<void> };
    if (nav.share) {
      await nav.share({ url }).catch(() => undefined);
      return;
    }
    await navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [url]);

  // Only the household side of the table manages the link.
  if (!player || player.isGuest) return null;

  return (
    <section
      data-testid="guest-seat-panel"
      className="flex flex-col gap-3 rounded-md border border-line bg-surface p-3"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-fg">
        <Link2 size={16} aria-hidden="true" /> The other seat is open
      </p>
      <p className="text-sm text-fg-2">
        Send this link to whoever you want to play. Whoever opens it first takes the seat.
      </p>

      {url ? (
        <>
          <code
            data-testid="guest-link-url"
            className="block overflow-x-auto whitespace-nowrap rounded-md border border-line px-2 py-2 text-xs text-fg-2"
          >
            {url}
          </code>
          <button
            type="button"
            data-testid="guest-link-share"
            onClick={() => void copy()}
            className="flex h-12 items-center justify-center gap-2 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
          >
            {typeof navigator !== 'undefined' && 'share' in navigator ? (
              <Share2 size={16} aria-hidden="true" />
            ) : (
              <Copy size={16} aria-hidden="true" />
            )}
            {copied ? 'Copied' : 'Send the link'}
          </button>
        </>
      ) : (
        <p className="text-sm text-fg-3" data-testid="guest-link-hidden">
          The link is only shown once. Make a new one if you need it again — the old one stops
          working.
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          data-testid="guest-link-regenerate"
          disabled={busy}
          onClick={() => void regenerate()}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-md border border-line text-sm font-semibold text-fg-2 disabled:opacity-50"
        >
          <RefreshCw size={14} aria-hidden="true" /> New link
        </button>
        <button
          type="button"
          data-testid="guest-link-cancel"
          disabled={busy}
          onClick={() => void cancel()}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-md border border-line text-sm font-semibold text-fg-2 disabled:opacity-50"
        >
          <X size={14} aria-hidden="true" /> Cancel link
        </button>
      </div>
    </section>
  );
}
